/**
 * The Inbox AI retry's choreography (#79) — the part of the lightbulb that is a sequence of
 * timed layout changes rather than state, kept out of the component so the component can stay
 * a few lines of "which phase are we in".
 *
 * What happens, in order, when the bulb is clicked:
 *
 *   1. FOLD. "Create task" and "Dismiss" collapse toward the right edge (their content is
 *      anchored right, so it is clipped from the left as the bulb sweeps over it) and the
 *      bulb's dropdown half narrows into a rounded cap on the bulb's right edge. The title is
 *      frozen at its current width first, so nothing above the buttons can re-wrap.
 *   2. MOVE. Part-way through the fold (`MOVE_STARTS_AT`) the retry cluster takes a row of its
 *      own — beneath the title on a wide card, in the actions row on a stacked one — and
 *      glides there from where the bulb had got to, taking the text area with it.
 *   3. REVEAL. The text area wipes out of the bulb, leftward, saying "Asking the AI…".
 *   4. ANSWER. The text area takes its final height once, then the answer types in.
 *
 * Clearing runs it backwards. A held *filed* item just fades out (it is resolved; there is
 * nothing to put back). Anything else wipes the text area toward the bulb while the cluster
 * rises to the first row, then — `HANDOFF_AT` of the way through that wipe, not after it — the
 * buttons grow out of the right edge and the bulb slides left as they do.
 *
 * Everything here was tuned by measuring element positions frame by frame in a mock, not by
 * eye, and most of the odd-looking lines are the fixes that fell out of that:
 *
 *   - Positions are measured *before* a layout change and the difference is played back as a
 *     transform (FLIP). Measure after, and whatever the change already moved is a one-frame jump.
 *   - The app's `--ease` is front-loaded (it starts at ~3.5× average speed), so every motion
 *     that begins from rest starts with a jerk. Sequential motions therefore overlap
 *     (`MOVE_STARTS_AT`, `HANDOFF_AT`) rather than waiting for the last one to settle, and the
 *     reset hand-off carries the cluster's current offset across the layout switch.
 *   - A button's width is measured and set inline before it folds. Animating `max-width` from
 *     a generous guess spends the first stretch of the animation visibly doing nothing.
 *
 * It does animate `max-width`, which §8.4 says not to (transform and opacity only, for the
 * sake of long lists). This is one control, triggered by a click, on a row that is not being
 * scrolled past — the reason for the rule does not apply, and a transform cannot make
 * neighbours reflow around a shrinking button.
 *
 * Under `prefers-reduced-motion` nothing animates: every state just swaps, in the same order.
 * The CSS zeroes the transitions; this file additionally skips the timers, so a reduced-motion
 * reader is not made to wait through a fold they cannot see.
 */

import { plainText, type Segment, type Tone } from "./inboxAiRetryMessage";
import type { AppSettings } from "./settings";

/** Fallbacks for the `--inbox-*-ms` custom properties on `.inbox-row` (styles.css owns them,
 * so the timing lives in one place; these only matter where no stylesheet is loaded). */
const FALLBACK = { fold: 1000, unfold: 1000, move: 700, reveal: 910 } as const;

/** How far into the fold the cluster starts moving to its own row. Early enough that the
 * bulb's slide to the right edge carries straight on into the move, late enough that the
 * buttons (whose opacity finishes at 30% of the fold) have already gone. */
const MOVE_STARTS_AT = 0.35;

/** How far into the reset wipe the cluster hands off from "text area row" to "first row" and
 * the buttons begin to grow. Earlier than the wipe's end because `--ease` front-loads the
 * rise: the bulb is all but home by then, and waiting for the wipe's tail left it standing
 * still for half a second. */
const HANDOFF_AT = 0.45;

const TYPE_MS = 16;
const LEAVE_MS = 500;

/** The text area tucks this far under the bulb, so its top and bottom lines run into the curve
 * of the bulb's corners. Mirrors `.inbox-ai-slot`'s negative margin in styles.css. */
const TUCK = "0.85rem";

export type InboxAiMotion = {
  /** Fold, move, reveal. Resolves once the text area is fully out and saying "Asking the AI…". */
  open(): Promise<void>;
  /** Give the text area its final height, then type the answer into it. Resolves with whether
   * the answer is longer than the two lines the text area shows (so it can offer to unfold). */
  answer(segments: Segment[], tone: Tone): Promise<boolean>;
  /** Show the whole answer, or go back to two lines. */
  expand(open: boolean): void;
  /** Put everything back, ending with the buttons grown out of the right edge. */
  reset(): Promise<void>;
  /** The item was filed and is being dismissed: fade the row out. */
  leave(): Promise<void>;
  /** Stop touching the DOM (the row unmounted mid-animation). */
  cancel(): void;
};

/**
 * How long a held answer waits before clearing itself, or `null` if it never does.
 *
 * Only a filed item's ✓ ever clears itself — a ✕ is a failure, and a failure is the thing you
 * need time to read — and only if the Display setting hasn't turned it off. Under reduced
 * motion it never does either: the ring that shows the countdown isn't drawn, and an invisible
 * countdown shouldn't dismiss anything.
 */
export function autoClearDelay(
  filed: boolean,
  settings: Pick<AppSettings, "inboxAiAutoClear" | "inboxAiAutoClearSeconds">,
  reducedMotion: boolean,
): number | null {
  if (!filed || reducedMotion || !settings.inboxAiAutoClear) return null;
  return Math.max(1, settings.inboxAiAutoClearSeconds) * 1000;
}

export function prefersReducedMotion(): boolean {
  // No `matchMedia` (jsdom without a stub) means nothing real is watching: skip the show.
  return (
    typeof window.matchMedia !== "function" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function need<T extends Element>(root: Element, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (!found) throw new Error(`inbox AI retry: missing ${selector}`);
  return found;
}

/** Write `segments` into `el`, up to `upTo` characters, optionally ending in a caret. Built
 * from DOM nodes and `textContent` — the text includes the person's own words. */
function render(el: HTMLElement, segments: Segment[], upTo = Infinity, caret = false) {
  el.textContent = "";
  let left = upTo;
  for (const segment of segments) {
    if (left <= 0) break;
    const text = segment.text.slice(0, left);
    left -= text.length;
    const node = document.createElement(segment.strong ? "strong" : "span");
    node.textContent = text;
    el.appendChild(node);
  }
  if (caret) {
    const node = document.createElement("span");
    node.className = "inbox-ai-caret";
    node.textContent = " ";
    el.appendChild(node);
  }
}

export function createInboxAiMotion(cluster: HTMLElement): InboxAiMotion {
  // An Inbox row, or any other host that marks itself (`data-ai-host`): the editors' forms wear
  // the same bulb, and fold their own buttons the same way.
  const owner = cluster.closest<HTMLElement>(".inbox-row, [data-ai-host]");
  if (!owner) throw new Error("inbox AI retry: not inside an .inbox-row or [data-ai-host]");
  const row: HTMLElement = owner;

  // Only a row with a title above its buttons has one to freeze.
  const title = row.querySelector<HTMLElement>(".title");
  const folders = Array.from(row.querySelectorAll<HTMLElement>(".inbox-fold"));
  const picker = need<HTMLElement>(cluster, ".capture-tier-picker");
  const sb = need<HTMLElement>(picker, ".split-button");
  const toggle = need<HTMLElement>(sb, ".split-button-toggle");
  const slot = need<HTMLElement>(cluster, ".inbox-ai-slot");
  const box = need<HTMLElement>(slot, ".inbox-ai-box");
  const text = need<HTMLElement>(box, ".inbox-ai-text");
  const sizer = need<HTMLElement>(text, ".inbox-ai-sizer");
  const typed = need<HTMLElement>(text, ".inbox-ai-typed");

  let cancelled = false;
  const timers = new Set<number>();
  const wait = (delay: number) =>
    new Promise<void>((resolve) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        resolve();
      }, delay);
      timers.add(id);
    });
  const later = (delay: number, fn: () => void) => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      if (!cancelled) fn();
    }, delay);
    timers.add(id);
  };

  const ms = (name: string, fallback: number) => {
    const value = parseFloat(getComputedStyle(row).getPropertyValue(name));
    return Number.isFinite(value) && value > 0 ? value : fallback;
  };

  /** FLIP: glide `el` from where `ref` was (`from`) to where layout has just put it. */
  function glide(el: HTMLElement, from: DOMRect, ref: HTMLElement, duration: number) {
    const to = ref.getBoundingClientRect();
    el.style.transition = "none";
    el.style.transform = `translate(${from.left - to.left}px, ${from.top - to.top}px)`;
    el.getBoundingClientRect();
    el.style.transition = `transform ${duration}ms var(--ease)`;
    el.style.transform = "";
    later(duration + 40, () => {
      el.style.transition = "";
    });
  }

  /** Animate the row's own height across a layout change that has already happened. */
  function glideHeight(from: number, duration: number) {
    const to = row.getBoundingClientRect().height;
    if (Math.abs(to - from) < 1) return;
    // Pinned taller than its content, `align-content: stretch` would spread the slack across
    // the line and nudge everything in it by a couple of pixels. Pack to the start instead.
    row.style.height = `${from}px`;
    row.style.overflow = "hidden";
    row.style.alignContent = "start";
    row.getBoundingClientRect();
    row.style.transition = `height ${duration}ms var(--ease)`;
    row.style.height = `${to}px`;
    later(duration + 40, clearHeight);
  }
  function clearHeight() {
    row.style.transition = "";
    row.style.height = "";
    row.style.overflow = "";
    row.style.alignContent = "";
  }

  const measure = (el: HTMLElement) => {
    el.style.maxWidth = `${el.getBoundingClientRect().width}px`;
  };

  function lockToggle(locked: boolean) {
    toggle.inert = locked;
    if (locked) toggle.setAttribute("aria-hidden", "true");
    else toggle.removeAttribute("aria-hidden");
  }

  function showThinking() {
    text.dataset.tone = "thinking";
    box.title = "";
    const thinking: Segment[] = [{ text: "Asking the AI…" }];
    render(sizer, thinking);
    render(typed, thinking, Infinity, true);
  }

  async function open() {
    const reduced = prefersReducedMotion();
    const fold = ms("--inbox-fold-ms", FALLBACK.fold);
    const move = ms("--inbox-move-ms", FALLBACK.move);
    const reveal = ms("--inbox-reveal-ms", FALLBACK.reveal);

    // Freeze the title where it is, so folding the buttons beside it cannot let it re-wrap.
    if (title) title.style.flex = `0 0 ${title.getBoundingClientRect().width}px`;
    row.classList.add("is-ai-active");

    if (reduced) {
      folders.forEach((el) => el.classList.add("is-folded", "is-gone"));
      toggle.classList.add("is-capped");
      sb.classList.add("is-capping");
      lockToggle(true);
      showThinking();
      cluster.classList.add("is-open", "is-boxed");
      return;
    }

    folders.forEach(measure);
    measure(toggle);
    lockToggle(true);
    row.getBoundingClientRect();
    folders.forEach((el) => el.classList.add("is-folded"));
    toggle.classList.add("is-capped");
    sb.classList.add("is-capping");

    await wait(fold * MOVE_STARTS_AT);
    if (cancelled) return;

    // Measure first: once the buttons leave layout the bulb has already jumped to its final x,
    // which would hide the rest of its slide from the glide. The whole cluster glides (not
    // just the bulb), so the text area emerges from the bulb as it travels.
    const from = picker.getBoundingClientRect();
    const fromHeight = row.getBoundingClientRect().height;
    folders.forEach((el) => el.classList.add("is-gone"));
    showThinking();
    cluster.classList.add("is-open", "is-boxed");
    glide(cluster, from, picker, move);
    glideHeight(fromHeight, move);
    await wait(Math.max(move, reveal));
  }

  /** Animate the text area from its current height to whatever `update` makes it. The bulb
   * stretches with it, so the outline stays one shape. */
  function resizeBox(update: () => void, reduced: boolean) {
    const before = box.getBoundingClientRect().height;
    update();
    const after = box.getBoundingClientRect().height;
    if (reduced || Math.abs(after - before) < 1) return;
    box.style.height = `${before}px`;
    box.getBoundingClientRect();
    box.style.transition = "height 0.6s var(--ease)";
    box.style.height = `${after}px`;
    later(640, () => {
      box.style.height = "";
      box.style.transition = "";
    });
  }

  /** Is the answer longer than the two lines the text area shows? */
  function overflowing(): boolean {
    return sizer.scrollHeight > sizer.clientHeight + 1;
  }

  function expand(open: boolean) {
    const reduced = prefersReducedMotion();
    resizeBox(() => text.classList.toggle("is-expanded", open), reduced);
  }

  async function answer(segments: Segment[], tone: Tone): Promise<boolean> {
    const reduced = prefersReducedMotion();
    const full = plainText(segments);
    text.dataset.tone = tone;
    box.title = full;
    // The final text is laid out invisibly from the start, so the box takes its final size
    // once and the typed text draws over it without the box growing line by line.
    resizeBox(() => {
      render(sizer, segments);
      typed.textContent = "";
    }, reduced);
    if (reduced) {
      render(typed, segments);
      return overflowing();
    }
    for (let i = 1; i <= full.length; i++) {
      render(typed, segments, i, true);
      await wait(TYPE_MS);
      if (cancelled) return false;
    }
    render(typed, segments);
    return overflowing();
  }

  function clearText() {
    text.classList.remove("is-expanded");
    sizer.textContent = "";
    typed.textContent = "";
    delete text.dataset.tone;
    box.title = "";
  }

  function reset(): Promise<void> {
    return new Promise<void>((resolve) => {
      const reduced = prefersReducedMotion();
      const unfold = ms("--inbox-unfold-ms", FALLBACK.unfold);
      const wipe = ms("--inbox-reveal-ms", FALLBACK.reveal);
      const handoff = wipe * HANDOFF_AT;

      // Where will the cluster live once it is back on the first row? Ask layout — no paint
      // happens between the two measurements.
      const r0 = cluster.getBoundingClientRect();
      const h0 = row.getBoundingClientRect().height;
      cluster.classList.remove("is-open");
      const r1 = cluster.getBoundingClientRect();
      const h1 = row.getBoundingClientRect().height;
      cluster.classList.add("is-open");
      const dx = r1.right - r0.right;
      const dy = r1.top - r0.top;

      // 2. Hand-off: the bulb is all but home, so the layout switches to the first row now and
      //    the buttons start growing out of the right edge. The text area leaves layout but
      //    keeps wiping, riding along with the cluster.
      function handOff() {
        const rise =
          typeof DOMMatrix === "function"
            ? new DOMMatrix(getComputedStyle(cluster).transform)
            : { m41: 0, m42: 0 };
        const laidOut = slot.getBoundingClientRect();
        slot.style.width = `${laidOut.width}px`;
        slot.style.height = `${laidOut.height}px`;
        slot.style.right = `calc(100% - ${TUCK})`;
        slot.classList.add("is-orphan");
        cluster.classList.remove("is-open");

        // Layout just moved the cluster by (dx, dy); counter that so nothing visibly jumps,
        // then carry on with whatever was left of the rise.
        cluster.style.transition = "none";
        cluster.style.transform = `translate(${rise.m41 - dx}px, ${rise.m42 - dy}px)`;
        cluster.getBoundingClientRect();
        cluster.style.transition = reduced
          ? ""
          : `transform ${Math.max(wipe - handoff, 1)}ms var(--ease)`;
        cluster.style.transform = "";

        folders.forEach((el) => el.classList.remove("is-gone"));
        [...folders, toggle, sb].forEach((el) => el.classList.add("is-unfolding"));
        row.getBoundingClientRect(); // commit the collapsed state before releasing it
        folders.forEach((el) => el.classList.remove("is-folded"));
        toggle.classList.remove("is-capped");
        sb.classList.remove("is-capping");

        // 4. Once it has all settled, hand everything back to normal layout.
        const settle = () => {
          [...folders, toggle, sb].forEach((el) => {
            el.classList.remove("is-unfolding");
            el.style.maxWidth = "";
          });
          lockToggle(false);
          if (title) title.style.flex = "";
          row.classList.remove("is-ai-active");
          resolve();
        };
        if (reduced) settle();
        else later(unfold + 60, settle);
      }

      // 3. The wipe has finished: drop the leftovers.
      function finish() {
        slot.classList.remove("is-orphan");
        slot.style.width = slot.style.height = slot.style.right = "";
        cluster.classList.remove("is-boxed");
        cluster.style.transition = "";
        cluster.style.transform = "";
        box.classList.remove("is-unrevealing");
        box.style.height = "";
        box.style.transition = "";
        clearHeight();
        clearText();
      }

      if (reduced) {
        handOff();
        finish();
        return;
      }

      // 1. Wipe + rise, together. The text area retreats toward the bulb (the reveal,
      //    reversed) while its height eases back to one line, and the whole cluster rises to
      //    the first row with it. The row's own height follows.
      box.classList.add("is-unrevealing");
      const boxFrom = box.getBoundingClientRect().height;
      const boxTo = parseFloat(getComputedStyle(box).minHeight);
      if (Number.isFinite(boxTo) && boxFrom - boxTo > 1) {
        box.style.height = `${boxFrom}px`;
        box.getBoundingClientRect();
        box.style.transition = `height ${wipe}ms var(--ease)`;
        box.style.height = `${boxTo}px`;
      }
      cluster.style.transition = "none";
      cluster.getBoundingClientRect();
      cluster.style.transition = `transform ${wipe}ms var(--ease)`;
      cluster.style.transform = `translate(${dx}px, ${dy}px)`;
      if (Math.abs(h1 - h0) >= 1) {
        row.style.height = `${h0}px`;
        row.style.overflow = "hidden";
        row.style.alignContent = "start";
        row.getBoundingClientRect();
        row.style.transition = `height ${wipe}ms var(--ease)`;
        row.style.height = `${h1}px`;
      }
      later(handoff, handOff);
      later(wipe + 40, finish);
    });
  }

  async function leave() {
    row.classList.add("is-leaving");
    if (!prefersReducedMotion()) await wait(LEAVE_MS);
  }

  function cancel() {
    cancelled = true;
    timers.forEach((id) => window.clearTimeout(id));
    timers.clear();
  }

  return { open, answer, expand, reset, leave, cancel };
}
